import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkDetailPage from '../../../components/work/WorkDetailPage';
import { contractTemplatesApi } from '../../../utils/workApi';
import { getContractTemplateFromResponse } from '../../../utils/workResponseUtils';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { Button, Card, Descriptions, message } from 'antd';
import { FileProtectOutlined, EditOutlined } from '@ant-design/icons';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const resolveCurrencyLabel = (currency) =>
  currency?.currency_name || currency?.currency_symbol || '—';

const ContractTemplateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/contracts/templates', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [tpl, setTpl] = useState(null);
  const [company, setCompany] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/contracts/templates',
    listLabel: 'قالب‌های قرارداد',
    entityLabel: tpl?.subject || (id ? `قالب #${id}` : 'قالب قرارداد'),
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await contractTemplatesApi.getById(id);
        setTpl(getContractTemplateFromResponse(res));
        setCompany(res?.company || null);
      } catch {
        message.error('خطا در بارگذاری');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <WorkDetailPage
      title={tpl?.subject || 'جزئیات قالب قرارداد'}
      subtitle="قالب قرارداد"
      icon={FileProtectOutlined}
      iconClassName="bg-amber-50 dark:bg-amber-900/20"
      iconColorClass="text-amber-600 dark:text-amber-400"
      backPath="/work/contracts/templates"
      onBack={goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
      loading={loading}
      headerExtra={
        <Button
          icon={<EditOutlined />}
          type="primary"
          className={PANEL_PRIMARY_BTN_CLASS}
          onClick={() =>
            navigateWithTrail(
              `/work/contracts/templates/${id}/edit`,
              tpl?.subject ? `ویرایش ${tpl.subject}` : `ویرایش قالب #${id}`,
              { returnTo: returnToRef.current || '/work/contracts/templates', ...buildGoBackState() },
            )
          }
          style={{ fontFamily: 'CustomFont' }}
        >
          ویرایش
        </Button>
      }
    >
      <Card className="shadow-sm border-0" style={{ fontFamily: 'CustomFont' }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label={label('موضوع')}>{tpl?.subject || '—'}</Descriptions.Item>
          <Descriptions.Item label={label('نوع قرارداد')}>
            {tpl?.contract_type?.name || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={label('مبلغ')}>
            {formatWorkPriceDisplay(tpl?.amount, { suffix: tpl?.currency?.currency_symbol })}
          </Descriptions.Item>
          <Descriptions.Item label={label('ارز')}>{resolveCurrencyLabel(tpl?.currency)}</Descriptions.Item>
          <Descriptions.Item label={label('شماره شرکت')}>{tpl?.company_id ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={label('توضیحات')} span={2}>
            <div className="whitespace-pre-wrap">{tpl?.description || tpl?.contract_detail || '—'}</div>
          </Descriptions.Item>
          {company ? (
            <>
              <Descriptions.Item label={label('شرکت')}>{company.company_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={label('تلفن شرکت')}>{company.company_phone || '—'}</Descriptions.Item>
            </>
          ) : null}
        </Descriptions>
      </Card>
    </WorkDetailPage>
  );
};

export default ContractTemplateView;
